import { PdpTechnicalSpecificationSectionUrlAnchor } from "@swo/shared";
import { type ProductDetailPageTranslation, type GeneralTranslation } from "@swo/storefront";
import type { SwarovskiOptikLocaleFrontend } from "@swo/storefront";
import type React from "react";
import { Spacer } from "../../components/_shared-components/arrangement/Space";
import type { Translated } from "../../domain/translations/translations";
import { nonEmpty } from "../../utils/fp/small-fp-utils";
import { ComponentRenderer } from "../_layout-helpers/component-renderer";
import { QUERYSTRING_PARAM } from "../product-comparison-page-layout/elements/state";
import { getAdditionalPdpData } from "./getAdditionalPdpData";
import { PDPDeliveryPackageDescription } from "./pdp-delivery-package-description";
import type { PDPSections } from "./pdp-navigation";
import { PdpProductComparisonComponent } from "./pdp-product-comparison-component";
import { ProductDetailPageCarousel } from "./PDP_AccessoriesCarousel/ProductDetailPageCarousel";
import { ProductDetailPageDownloadsCarousel } from "./PDP_DownloadsCarousel/ProductDetailPageDownloadsCarousel";
import { IconsAndTextsPdpComponent } from "./PDP_IconsAndTexts/IconsAndTextsPdpComponent";
import type { PDPState } from "./PDP_SingleImageCarousel/availability/use-product-variant-state";
import { REVIEWS_SECTION_ID } from "./PDP_SingleImageCarousel/REVIEWS_SECTION_ID";
import { ProductDetailPageTechnicalSpecification } from "./PDP_TechnicalSpecification/ProductDetailPageTechnicalSpecification";
import type { PDPMainComponentProps } from "./PDPMainComponent";
import { PDPReviewsDetails } from "./PDPReviewsDetails";
import { renderNonEmpty } from "./renderNonEmpty";
import type { ReviewTranslations } from "./reviewTranslations";
import { ProReviews } from "./ProReviews";

export function getSections(
  pdpState: Pick<PDPState, "selectedProduct" | "selectedVariant">,
  data: PDPMainComponentProps["data"],
  {
    t,
    gt,
    reviewsT,
  }: {
    t: Translated<ProductDetailPageTranslation>;
    gt: Translated<GeneralTranslation>;
    reviewsT: ReviewTranslations;
  },
  locale: SwarovskiOptikLocaleFrontend,
): PDPSections {
  const comparisonPageLink = {
    pathname: data.links.comparisonPageLink,
    search: `?${QUERYSTRING_PARAM}=${pdpState.selectedProduct.key}`,
  };

  const { techSpecsAttributes, deliveryPackageProps, longDescription } = getAdditionalPdpData(
    pdpState.selectedProduct,
    pdpState.selectedVariant,
    {
      yesLabel: data.translations.techSpec.yesLabel.value,
      noLabel: data.translations.techSpec.noLabel.value,
    },
    locale.country === "INTERNATIONAL" ? "AT" : locale.country.isoCode,
  );

  const compareProductsLabel = t("product_detail_page.compareProductsLabel");

  return [
    {
      label: t("navigation.aboveTheFoldContentHeadline"),
      anchor: "root",
      mobileHidden: false,
    },
    data.components.iconsAndTextsPdpComponent || data.components.contentArea.first.length > 0
      ? {
          label: t("navigation.contentAreaHeadline"),
          hidden: true,
          node: (
            <>
              {data.components.iconsAndTextsPdpComponent && (
                <IconsAndTextsPdpComponent {...data.components.iconsAndTextsPdpComponent} />
              )}
              {renderNonEmpty(data.components.contentArea.first, components => (
                <>
                  <Spacer size={"md"} />
                  <ComponentRenderer
                    components={components}
                    localeForNumberFormat={locale}
                    translations={gt}
                    noSpaceForFirstComponent={true}
                  />
                </>
              ))}
            </>
          ),
        }
      : null,
    deliveryPackageProps || longDescription
      ? {
          label: gt("moreInfoLabel"),
          node: (
            <PDPDeliveryPackageDescription
              gt={gt}
              deliveryPackageProps={deliveryPackageProps}
              longDescription={longDescription}
            />
          ),
        }
      : undefined,
    renderNonEmpty(data.technicalSpecification, sections => ({
      label: t("technicalSpecificationHeadline"),
      anchor: PdpTechnicalSpecificationSectionUrlAnchor,
      node: (
        <ProductDetailPageTechnicalSpecification
          technicalSpecificationHeadline={t("technicalSpecificationHeadline")}
          moreInfoLabel={gt("moreInfoLabel")}
          sections={sections}
        />
      ),
    })),
    {
      label: compareProductsLabel,
      node: (
        <PdpProductComparisonComponent
          link={comparisonPageLink}
          label={compareProductsLabel}
          selectedProduct={pdpState.selectedProduct}
          selectedVariant={pdpState.selectedVariant}
          locale={locale}
          techSpecsAttributes={techSpecsAttributes}
        />
      ),
    },
    renderNonEmpty(data.components.downloads, downloads => ({
      label: t("downloadsHeadline"),
      node: <ProductDetailPageDownloadsCarousel downloadsHeadline={t("downloadsHeadline")} downloads={downloads} />,
    })),
    renderNonEmpty(data.proReviews, proReviews => ({
      label: t("proReviewsHeadline"),
      node: (
        <>
          <Spacer size={"lg"} />
          <ProReviews reviews={proReviews} t={reviewsT} />
        </>
      ),
    })),
    renderNonEmpty(data.components.contentArea.second, components => ({
      label: t("navigation.secondContentAreaHeadline"),
      hidden: true,
      node: (
        <>
          <Spacer size={"md"} />
          <ComponentRenderer
            components={components}
            localeForNumberFormat={locale}
            translations={gt}
            noSpaceForFirstComponent={true}
          />
        </>
      ),
    })),
    renderNonEmpty(data.related.accessories, products => ({
      label: t("accessoriesHeadline"),
      node: <ProductDetailPageCarousel headline={t("accessoriesHeadline")} products={products} />,
    })),
    renderNonEmpty(data.related.spareParts, products => ({
      label: t("sparePartsHeadline"),
      hidden: true,
      node: <ProductDetailPageCarousel headline={t("sparePartsHeadline")} products={products} />,
    })),
    data.selectedProduct.reviews
      ? {
          label: reviewsT("reviewsHeadline"),
          hidden: false,
          node: (
            <PDPReviewsDetails
              id={REVIEWS_SECTION_ID}
              headline={reviewsT("reviewsHeadline")}
              data={data.selectedProduct.reviews}
              t={reviewsT}
              gt={gt}
            />
          ),
        }
      : null,
    renderNonEmpty(data.related.products, products => ({
      label: t("relatedProductsHeadline"),
      hidden: true,
      node: (
        <ProductDetailPageCarousel
          headline={t("relatedProductsHeadline")}
          products={products}
          theme={"wide-with-background"}
        />
      ),
    })),
  ].filter(nonEmpty);
}
